import { Box, Flex, Text } from "@mantine/core";
import {
  IconTarget,
  IconBriefcase,
  IconUserCheck,
} from "@tabler/icons-react";
import ProfileCollapsibleSection from "@/components/shared/ProfileCollapsibleSection";
import {
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
} from "@/components/shared/constants/general.const";

type CollaborationIntentBelowWhatWeOfferProps = {
  collaboration: any;
  /** Displayed company profile (other party in the match). */
  company: any;
  /** Viewer sent the request — role labels use “your / expected partner” wording. */
  isRequester: boolean;
  viewerCollaborationIntent?: string[];
};

/**
 * Request title, summary, collaboration type, and role fields — placed directly under
 * “What We Offer” and above the first navy accordion (Business Overview).
 */
export default function CollaborationIntentBelowWhatWeOffer({
  collaboration,
  company,
  isRequester,
  viewerCollaborationIntent,
}: CollaborationIntentBelowWhatWeOfferProps) {
  const profileCollaborationIntents = isRequester
    ? Array.isArray(viewerCollaborationIntent)
      ? viewerCollaborationIntent
      : []
    : Array.isArray(company.collaborationIntent)
      ? company.collaborationIntent
      : [];
  const hasProfileCollaborationIntents = profileCollaborationIntents.length > 0;

  const hasTitle =
    typeof collaboration.expressInterestTitle === "string" &&
    collaboration.expressInterestTitle.trim() !== "";
  const hasSummary =
    typeof collaboration.expressInterestSummary === "string" &&
    collaboration.expressInterestSummary.trim() !== "";

  const showIntentBlock =
    collaboration &&
    (collaboration.requesterRole ||
      collaboration.expectedPartnerRole ||
      hasProfileCollaborationIntents ||
      hasTitle ||
      hasSummary);

  if (!showIntentBlock) return null;

  return (
    <ProfileCollapsibleSection
      title="Collaboration Intent Details"
      icon={<IconBriefcase size={18} color="#ffc300" stroke={1.5} />}
      defaultOpen
      mb="md"
    >
      <Text size="xs" c="slate.5" mb="lg">
        Information provided by the company about their collaboration goals
      </Text>

      <Flex direction="column" gap="md">
        {hasTitle || hasSummary ? (
          <Box>
            {hasTitle ? (
              <Box mb={hasSummary ? "md" : 0}>
                <Text size="xs" c="slate.6" tt="uppercase" fw={400} mb="xs">
                  Title
                </Text>
                <Text
                  size="sm"
                  fw={600}
                  c="slate.8"
                  style={{ lineHeight: 1.5, wordBreak: "break-word" }}
                >
                  {collaboration.expressInterestTitle.trim()}
                </Text>
              </Box>
            ) : null}
            {hasSummary ? (
              <Box>
                <Text size="xs" c="slate.6" tt="uppercase" fw={400} mb="xs">
                  Summary
                </Text>
                <Text
                  size="sm"
                  c="slate.7"
                  style={{ lineHeight: 1.55, wordBreak: "break-word", textAlign: "justify" }}
                >
                  {collaboration.expressInterestSummary.trim()}
                </Text>
              </Box>
            ) : null}
          </Box>
        ) : null}

        {hasProfileCollaborationIntents && (
          <Flex align="center" gap="sm" wrap="wrap">
            <IconTarget
              size={14}
              color="var(--mantine-color-violet-6)"
              style={{ flexShrink: 0 }}
            />
            <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
              Collaboration type
            </Text>
            <Text size="sm" c="slate.7" fw={500}>
              {profileCollaborationIntents
                .map((intent: string) => getCollaborationIntentLabel(intent))
                .join(", ")}
            </Text>
          </Flex>
        )}

        {(collaboration.requesterRole || collaboration.expectedPartnerRole) && (
          <Flex direction="column" gap="md" w="100%">
            {collaboration.requesterRole && (
              <Flex align="center" gap="sm" wrap="wrap">
                <IconBriefcase
                  size={14}
                  color="var(--mantine-color-teal-6)"
                  style={{ flexShrink: 0 }}
                />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  {isRequester
                    ? "Your role in this collaboration"
                    : "Their role in this collaboration"}
                </Text>
                <Text size="sm" c="slate.7" fw={500}>
                  {getCollaborationRoleLabel(collaboration.requesterRole)}
                </Text>
              </Flex>
            )}

            {collaboration.expectedPartnerRole && (
              <Flex align="center" gap="sm" wrap="wrap">
                <IconUserCheck
                  size={14}
                  color="var(--mantine-color-yellow-7)"
                  style={{ flexShrink: 0 }}
                />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  {isRequester ? "Expected Partner Role" : "What they expect from you"}
                </Text>
                <Text size="sm" c="slate.7" fw={500}>
                  {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </ProfileCollapsibleSection>
  );
}
